import { type IOrganisation } from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  FirestoreMigrate,
  unserialise,
  type SerialisedData,
  type WithRef,
} from '@principle-theorem/shared';
import { AutomationConfiguration } from '../models/automation/automation-configuration';
import { Role } from '../models/auth/role';
import { Brand } from '../models/brand';
import { ConditionConfiguration } from '../models/clinical-charting/conditions/condition-configuration';
import { FeeSchedule } from '../models/clinical-charting/fees/fee-schedule/fee-schedule';
import { MultiTreatmentConfiguration } from '../models/clinical-charting/treatment/multi-treatment-configuration';
import { TreatmentConfiguration } from '../models/clinical-charting/treatment/treatment-configuration';
import { CustomFormConfiguration } from '../models/custom-forms/custom-form-configuration';
import { Organisation } from '../models/organisation/organisation';
import { Practice } from '../models/practice/practice';
import { Roster } from '../models/staffer/roster';
import { ManualTransactionType } from '../models/transaction/manual-transaction-type';
import {
  type IExportedBrand,
  type IExportedOrganisation,
} from './practice-migration-organisation-exporter';

export class PracticeMigrationOrganisationImporter {
  async importData(
    serialisedData: SerialisedData<IExportedOrganisation>
  ): Promise<void> {
    const data = unserialise<IExportedOrganisation>(serialisedData);
    await FirestoreMigrate.upsertDoc(
      Organisation.col(),
      data.organisation,
      data.organisation.ref.id
    );

    await asyncForEach(data.feeSchedules, (feeSchedule) =>
      FirestoreMigrate.upsertDoc(
        FeeSchedule.col(data.organisation),
        feeSchedule,
        feeSchedule.ref.id
      )
    );

    await asyncForEach(data.users, (user) =>
      FirestoreMigrate.upsertDoc(
        Organisation.userCol(data.organisation),
        user,
        user.ref.id
      )
    );

    await asyncForEach(data.roles, (role) =>
      FirestoreMigrate.upsertDoc(
        Organisation.roleCol(data.organisation),
        {
          ...role,
          permissions: Role.withCalendarEventsManagePermission(
            role.permissions
          ),
        },
        role.ref.id
      )
    );

    await asyncForEach(data.brands, async (brand) =>
      this._importBrandData(data.organisation, brand)
    );
  }

  private async _importBrandData(
    organisation: WithRef<IOrganisation>,
    brand: IExportedBrand
  ): Promise<void> {
    const brandRef = { ref: brand.brand.ref };

    await FirestoreMigrate.upsertDoc(
      Organisation.brandCol(organisation),
      brand.brand,
      brand.brand.ref.id
    );

    await asyncForEach(brand.appointmentTags, (appointmentTag) =>
      FirestoreMigrate.upsertDoc(
        Brand.appointmentTagCol(brandRef),
        appointmentTag,
        appointmentTag.ref.id
      )
    );

    await asyncForEach(
      brand.automationConfigurations,
      (automationConfiguration) =>
        FirestoreMigrate.upsertDoc(
          AutomationConfiguration.col(brandRef),
          automationConfiguration,
          automationConfiguration.ref.id
        )
    );

    await asyncForEach(brand.eventTags, (eventTag) =>
      FirestoreMigrate.upsertDoc(
        Brand.eventTagCol(brandRef),
        eventTag,
        eventTag.ref.id
      )
    );

    await asyncForEach(brand.patientNoteTags, (patientNoteTag) =>
      FirestoreMigrate.upsertDoc(
        Brand.patientNoteTagCol(brandRef),
        patientNoteTag,
        patientNoteTag.ref.id
      )
    );

    await asyncForEach(brand.patientTags, (patientTag) =>
      FirestoreMigrate.upsertDoc(
        Brand.patientTagCol(brandRef),
        patientTag,
        patientTag.ref.id
      )
    );

    await asyncForEach(brand.practices, async (practice) => {
      await FirestoreMigrate.upsertDoc(
        Brand.practiceCol(brandRef),
        {
          ...practice.practice,
          enabledForSchedulingAggregateTrial: false,
        },
        practice.practice.ref.id
      );

      await asyncForEach(practice.mediaTags, async (mediaTag) => {
        await FirestoreMigrate.upsertDoc(
          Practice.mediaTagCol(practice.practice),
          mediaTag,
          mediaTag.ref.id
        );
      });

      await asyncForEach(practice.feeSchedules, async (feeSchedule) => {
        await FirestoreMigrate.upsertDoc(
          FeeSchedule.col(practice.practice),
          feeSchedule,
          feeSchedule.ref.id
        );
      });
    });

    await asyncForEach(brand.products, (product) =>
      FirestoreMigrate.upsertDoc(
        Brand.productCol(brandRef),
        product,
        product.ref.id
      )
    );

    await asyncForEach(brand.referralSources, (referralSource) =>
      FirestoreMigrate.upsertDoc(
        Brand.referralSourceCol(brandRef),
        referralSource,
        referralSource.ref.id
      )
    );

    await asyncForEach(brand.manualTransactionTypes, (manualTransactionType) =>
      FirestoreMigrate.upsertDoc(
        ManualTransactionType.col(brandRef),
        manualTransactionType,
        manualTransactionType.ref.id
      )
    );

    await asyncForEach(brand.snippets, (snippet) =>
      FirestoreMigrate.upsertDoc(
        Brand.snippetCol(brandRef),
        snippet,
        snippet.ref.id
      )
    );

    await asyncForEach(brand.staffWithRosters, async (stafferWithRoster) => {
      await FirestoreMigrate.upsertDoc(
        Brand.stafferCol(brandRef),
        stafferWithRoster.staffer,
        stafferWithRoster.staffer.ref.id
      );
      await asyncForEach(stafferWithRoster.rosters, (roster) =>
        FirestoreMigrate.upsertDoc(
          Roster.col(stafferWithRoster.staffer),
          roster,
          roster.ref.id
        )
      );
    });

    await asyncForEach(brand.teams, (team) =>
      FirestoreMigrate.upsertDoc(Brand.teamCol(brandRef), team, team.ref.id)
    );

    await asyncForEach(brand.templates, (template) =>
      FirestoreMigrate.upsertDoc(
        Brand.templateCol(brandRef),
        template,
        template.ref.id
      )
    );

    await asyncForEach(brand.treatmentCategories, (treatmentCategory) =>
      FirestoreMigrate.upsertDoc(
        Brand.treatmentCategoryCol(brandRef),
        treatmentCategory,
        treatmentCategory.ref.id
      )
    );

    await asyncForEach(brand.sterilisationMachines, (sterilisationMachine) =>
      FirestoreMigrate.upsertDoc(
        Brand.sterilisationMachineCol(brandRef),
        sterilisationMachine,
        sterilisationMachine.ref.id
      )
    );

    await asyncForEach(brand.sterilisationPacks, (sterilisationPack) =>
      FirestoreMigrate.upsertDoc(
        Brand.sterilisationPacksCol(brandRef),
        sterilisationPack,
        sterilisationPack.ref.id
      )
    );

    await asyncForEach(
      brand.sterilisationCycleTypes,
      (sterilisationCycleType) =>
        FirestoreMigrate.upsertDoc(
          Brand.sterilisationCycleTypesCol(brandRef),
          sterilisationCycleType,
          sterilisationCycleType.ref.id
        )
    );

    await asyncForEach(brand.sterilisationEquipment, (sterilisationEquipment) =>
      FirestoreMigrate.upsertDoc(
        Brand.sterilisationEquipmentCol(brandRef),
        sterilisationEquipment,
        sterilisationEquipment.ref.id
      )
    );

    await asyncForEach(brand.prescriptionItems, (prescriptionItem) =>
      FirestoreMigrate.upsertDoc(
        Brand.prescriptionItemCol(brandRef),
        prescriptionItem,
        prescriptionItem.ref.id
      )
    );

    await asyncForEach(brand.cancellationReasons, (cancellationReason) =>
      FirestoreMigrate.upsertDoc(
        Brand.cancellationReasonCol(brandRef),
        cancellationReason,
        cancellationReason.ref.id
      )
    );

    await asyncForEach(
      brand.patientMetadataDisplays,
      (patientMetadataDisplay) =>
        FirestoreMigrate.upsertDoc(
          Brand.patientMetadataDisplayCol(brandRef),
          patientMetadataDisplay,
          patientMetadataDisplay.ref.id
        )
    );

    await asyncForEach(
      brand.treatmentConfigurations,
      (treatmentConfiguration) =>
        FirestoreMigrate.upsertDoc(
          TreatmentConfiguration.col(brandRef),
          treatmentConfiguration,
          treatmentConfiguration.ref.id
        )
    );

    await asyncForEach(
      brand.multiTreatmentConfigurations,
      (multiTreatmentConfiguration) =>
        FirestoreMigrate.upsertDoc(
          MultiTreatmentConfiguration.col(brandRef),
          multiTreatmentConfiguration,
          multiTreatmentConfiguration.ref.id
        )
    );

    await asyncForEach(
      brand.conditionConfigurations,
      (conditionConfiguration) =>
        FirestoreMigrate.upsertDoc(
          ConditionConfiguration.col(brandRef),
          conditionConfiguration,
          conditionConfiguration.ref.id
        )
    );

    await asyncForEach(brand.treatmentTemplates, (treatmentTemplate) =>
      FirestoreMigrate.upsertDoc(
        Brand.treatmentTemplateCol(brandRef),
        treatmentTemplate,
        treatmentTemplate.ref.id
      )
    );

    await asyncForEach(
      brand.customFormConfigurations,
      async (customFormConfiguration) => {
        await FirestoreMigrate.upsertDoc(
          Brand.customFormConfigCol(brandRef),
          customFormConfiguration.customFormConfiguration,
          customFormConfiguration.customFormConfiguration.ref.id
        );

        await asyncForEach(customFormConfiguration.history, (history) =>
          FirestoreMigrate.upsertDoc(
            CustomFormConfiguration.contentHistoryCol(
              customFormConfiguration.customFormConfiguration
            ),
            history,
            history.ref.id
          )
        );
      }
    );
  }
}
